import React from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { books_getInventory, books_upsertBook, books_adjustReceived } from "../../lib/firestoreAdapters";
import { useOutletContext } from "react-router-dom";

function keyInv(campus, sy) { return `books_inventory_${String(campus).toLowerCase()}_${sy}`; }
function loadInv(campus, sy) { try { const raw = localStorage.getItem(keyInv(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveInv(campus, sy, data) { try { localStorage.setItem(keyInv(campus, sy), JSON.stringify(data)); } catch (e) { void 0; } }

function parseCSV(text) {
  const rows = text.split(/\r?\n/).filter(Boolean);
  if (!rows.length) return [];
  const headers = rows[0].split(",").map(h => h.trim());
  return rows.slice(1).map(line => {
    const cols = line.split(",").map(c => c.trim());
    const r = {};
    headers.forEach((h, i) => r[h] = cols[i] || "");
    return r;
  });
}

export default function BooksInventory() {
  const { campus, schoolYear } = useOutletContext();
  const [list, setList] = React.useState(() => loadInv(campus, schoolYear));
  const [q, setQ] = React.useState("");
  const [showReceive, setShowReceive] = React.useState(false);
  const [rcv, setRcv] = React.useState({ supplier: "", drNo: "", date: new Date().toISOString().slice(0,10), csv: "" });
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    async function fetch() {
      if (USE_LOCAL_FALLBACK) {
        setList(loadInv(campus, schoolYear));
      } else {
        const data = await books_getInventory(campus, schoolYear);
        setList(data);
      }
    }
    fetch();
  }, [campus, schoolYear]);

  async function onImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const txt = String(reader.result || "");
      const arr = parseCSV(txt).map(r => ({
        title: r.title || r.Title || r["Book Title"] || "",
        isbn: r.isbn || r.ISBN || "",
        publisher: r.publisher || r.Publisher || "",
        grade: r.grade || r.Grade || "",
        cost: Number(r.cost || r.Cost || 0),
        price: Number(r.price || r.Price || 0),
        received: Number(r.received || r.Received || r.Stock || 0),
        sold: Number(r.sold || r.Sold || 0),
      }));
      if (USE_LOCAL_FALLBACK) {
        const merged = merge(list, arr);
        setList(merged);
        saveInv(campus, schoolYear, merged);
      } else {
        for (const b of arr) {
          await books_upsertBook(campus, schoolYear, b);
        }
        const data = await books_getInventory(campus, schoolYear);
        setList(data);
      }
    };
    reader.readAsText(file);
  }
  function merge(existing, imported) {
    const map = {};
    for (const r of existing) map[`${r.title}|${r.isbn}|${r.grade}`] = r;
    for (const r of imported) {
      const k = `${r.title}|${r.isbn}|${r.grade}`;
      if (!map[k]) map[k] = r;
      else {
        map[k] = { ...map[k], ...r, received: Number(map[k].received || 0) + Number(r.received || 0) };
      }
    }
    return Object.values(map);
  }
  async function adjust(i, delta) {
    if (USE_LOCAL_FALLBACK) {
      const next = list.slice();
      next[i] = { ...next[i], received: Number(next[i].received || 0) + delta };
      setList(next); saveInv(campus, schoolYear, next);
    } else {
      const b = list[i];
      await books_adjustReceived(campus, schoolYear, b.id, delta);
      const data = await books_getInventory(campus, schoolYear);
      setList(data);
    }
  }
  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(b => String(b.title).toLowerCase().includes(s) || String(b.grade).toLowerCase().includes(s) || String(b.publisher).toLowerCase().includes(s));
  }, [list, q]);

  function parseItems(text) {
    const rows = text.split(/\r?\n/).filter(Boolean);
    if (!rows.length) return [];
    const splitSmart = (line) => {
      if (line.includes(",")) return line.split(",").map(s => s.trim());
      // fallback: split by 2+ spaces or tabs
      return line.trim().split(/\s{2,}|\t+/).map(s => s.trim()).filter(Boolean);
    };
    const normalizeHeader = (h) => h.toLowerCase().replace(/\s+/g, "");
    const headersRaw = splitSmart(rows[0]);
    const headers = headersRaw.map(normalizeHeader);
    const out = [];
    for (let i = 1; i < rows.length; i++) {
      const cols = splitSmart(rows[i]);
      if (!cols.length) continue;
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = cols[idx] || ""; });
      const num = (v) => {
        const s = String(v || "").replace(/₱/g, "").replace(/,/g, "").trim();
        const n = Number(s); return isNaN(n) ? 0 : n;
      };
      out.push({
        isbn: obj["isbn"] || "",
        title: obj["title"] || "",
        grade: obj["grade"] || "",
        publisher: obj["publisher"] || obj["supplier"] || "",
        cost: num(obj["cost"]),
        qty: num(obj["qty"] || obj["quantity"] || obj["received"])
      });
    }
    return out;
  }
  async function createReceipt(e) {
    e.preventDefault();
    const items = parseItems(rcv.csv).filter(it => it.isbn || it.title);
    if (items.length === 0) { alert("No items parsed. Please paste CSV with headers: isbn,title,grade,publisher,cost,qty"); return; }
    setSaving(true);
    try {
      if (USE_LOCAL_FALLBACK) {
        // Store as local note and adjust inventory directly (dev mode)
        const next = list.slice();
        items.forEach(it => {
          const idx = next.findIndex(b => (b.isbn||"")===it.isbn || (b.title||"")===it.title);
          if (idx >= 0) next[idx].received = Number(next[idx].received || 0) + Number(it.qty || 0);
          else next.push({ title: it.title, isbn: it.isbn, publisher: it.publisher, grade: it.grade, cost: it.cost, price: it.cost, received: Number(it.qty || 0), sold: 0 });
        });
        setList(next); saveInv(campus, schoolYear, next);
        alert("Delivery recorded (local).");
      } else {
        const { addDoc, collection } = await import("firebase/firestore");
        const { db } = await import("../../services/firebase");
        await addDoc(collection(db, "books_receipts"), {
          campus, schoolYear,
          supplier: rcv.supplier, drNo: rcv.drNo, date: rcv.date,
          status: "To Receive",
          items,
          createdAt: new Date().toISOString()
        });
        alert("Delivery created in 'To Receive'. Move to 'Received' in Deliveries to update inventory.");
      }
      setShowReceive(false);
      setRcv({ supplier: "", drNo: "", date: new Date().toISOString().slice(0,10), csv: "" });
    } finally {
      setSaving(false);
    }
  }

  async function removeBook(index) {
    const b = list[index];
    if (!b) return;
    if (Number(b.sold || 0) > 0) { alert("Cannot delete: this title has sales recorded."); return; }
    const ok = window.confirm(`Delete "${b.title}" from inventory?`);
    if (!ok) return;
    if (USE_LOCAL_FALLBACK) {
      const next = list.filter((_, i) => i !== index);
      setList(next); saveInv(campus, schoolYear, next);
      return;
    }
    try {
      const { deleteDoc, doc } = await import("firebase/firestore");
      const { db } = await import("../../services/firebase");
      await deleteDoc(doc(db, "books_inventory", String(b.id)));
      const next = list.filter((_, i) => i !== index);
      setList(next);
    } catch (e) {
      alert("Failed to delete in Firestore. Check permissions.");
    }
  }

  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">
          Development mode: using local browser storage. In production this will use Firestore.
        </div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Inventory — {campus.toUpperCase()} — {schoolYear}</h5>
        <div className="d-flex gap-2">
          <input className="form-control" style={{ width: 280 }} placeholder="Search title/grade/publisher" value={q} onChange={e => setQ(e.target.value)} />
          <input type="file" accept=".csv" className="form-control" onChange={onImport} title="Import CSV (Title, ISBN, Publisher, Grade, Cost, Price, Received)" />
          <button className="btn btn-success" onClick={() => setShowReceive(true)}>Receive Delivery</button>
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th>Title</th><th>Grade</th><th>Publisher</th><th>ISBN</th>
              <th className="text-end">Cost</th><th className="text-end">Price</th>
              <th className="text-end">Received</th><th className="text-end">Sold</th><th className="text-end">On Hand</th>
              <th className="text-center">Adjust</th>
              <th className="text-center">Delete</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="11" className="text-center text-muted">No books yet. Import CSV to seed this school year.</td></tr>
            ) : filtered.map((b, i) => {
              const onHand = Number(b.received || 0) - Number(b.sold || 0);
              return (
                <tr key={b.id}>
                  <td>{b.title}</td><td>{b.grade}</td><td>{b.publisher}</td><td>{b.isbn || "-"}</td>
                  <td className="text-end">₱{Number(b.cost || 0).toLocaleString()}</td>
                  <td className="text-end">₱{Number(b.price || 0).toLocaleString()}</td>
                  <td className="text-end">{Number(b.received || 0)}</td>
                  <td className="text-end">{Number(b.sold || 0)}</td>
                  <td className="text-end">{onHand}</td>
                  <td className="text-center">
                    <div className="btn-group btn-group-sm">
                      <button className="btn btn-outline-secondary" onClick={() => adjust(i, -1)}>-1</button>
                      <button className="btn btn-outline-secondary" onClick={() => adjust(i, 1)}>+1</button>
                    </div>
                  </td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-danger" disabled={Number(b.sold || 0) > 0} title={Number(b.sold || 0) > 0 ? "Cannot delete: has sales" : "Delete title"} onClick={() => removeBook(i)}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showReceive && (
        <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.35)", zIndex: 1050 }}>
          <div className="position-absolute top-50 start-50 translate-middle" style={{ width: "min(900px, 95vw)" }}>
            <div className="card shadow-lg">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Receive Delivery</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowReceive(false)}>Close</button>
              </div>
              <div className="card-body">
                <form className="row g-3" onSubmit={createReceipt}>
                  <div className="col-md-4"><label className="form-label">Supplier/Publisher</label><input className="form-control" value={rcv.supplier} onChange={e => setRcv(f => ({ ...f, supplier: e.target.value }))} required /></div>
                  <div className="col-md-4"><label className="form-label">DR / Invoice #</label><input className="form-control" value={rcv.drNo} onChange={e => setRcv(f => ({ ...f, drNo: e.target.value }))} /></div>
                  <div className="col-md-4"><label className="form-label">Date</label><input type="date" className="form-control" value={rcv.date} onChange={e => setRcv(f => ({ ...f, date: e.target.value }))} required /></div>
                  <div className="col-12">
                    <label className="form-label">Items CSV</label>
                    <textarea className="form-control" rows="6" placeholder="isbn,title,grade,publisher,cost,qty" value={rcv.csv} onChange={e => setRcv(f => ({ ...f, csv: e.target.value }))}></textarea>
                    <div className="form-text">Paste CSV with headers: isbn,title,grade,publisher,cost,qty — commas OR multi-space/tab delimited are accepted</div>
                  </div>
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowReceive(false)}>Cancel</button>
                    <button className="btn btn-primary" disabled={saving}>Create Delivery</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
